import { Screen, ScreenTitle } from '../ui/Screen.jsx'
import { useConfigBundle, screensOf } from '../config.js'

/**
 * (a) THE END OF THE STUDY. AD-11 §2.1, DS-3 E2.
 *
 * Reached from ONE fact and never from an inference: `study_complete` on
 * GET /api/beeps/current (backend #133), the same name and the same source
 * POST submit returns.
 *
 * ⚠️ NEVER DERIVE THIS FROM `completed === of`. It is wrong for almost
 * everyone. The study ends when the last beep is submitted OR expires, so a
 * participant who missed any beep never reaches 30 submitted and would sit on a
 * counter forever, told nothing had ended. Missing beeps is expected and
 * logged; an ending that only fires for a perfect participant is an ending that
 * mostly does not fire.
 *
 * This screen existed, written and correct, and was UNREACHABLE for the whole
 * project: `current` did not carry the fact, so a finished participant fell
 * through to the counter branch and read "We'll send you a reminder when the
 * next one is ready." Five days, 499 answers, and the last thing she read was a
 * promise nobody would keep.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * THE LINE BETWEEN A THANK-YOU AND A GRADE, because this is the guard that
 * will get "improved" away by someone being kind.
 *
 *   THE TEST: could the sentence be true of every participant regardless of
 *   how much she answered?
 *
 *     "Thank you for taking part"        true of everyone.   A COURTESY.
 *     "Thank you for completing all 30"  depends on her.     A GRADE.
 *
 * Thank her for the TIME, which every participant gave equally, never for the
 * ANSWERS, which they did not. This screen has to read right for a woman who
 * missed twelve beeps.
 *
 * STILL FORBIDDEN, unchanged: any count, any adherence figure, any summary of
 * her five days, any "well done", any streak, any total, any completion date.
 * THE ONE NUMBER THAT MAY APPEAR IS NONE.
 *
 * And the other half, which is why the screen exists at all: a study that takes
 * five days of disclosure and ends without thanking her is not neutral. It is
 * cold, and cold is its own judgement.
 * ═══════════════════════════════════════════════════════════════════════
 *
 * THE VISUAL TREATMENT IS THE ENDING (AD-11 §2.1):
 *
 *  - NO CARD. Every other home state is a card on --surface-raised; this one is
 *    bare page. §4.7's test is that a card is earned by a group of things that
 *    must be read or answered together, and a closing statement is neither.
 *    Removing the container is what makes this read as a full stop rather than
 *    as one more status tile, and the contrast against every home state she has
 *    seen for five days IS the ending. It was a card and that was the defect.
 *  - NO PRIMARY ACTION, and this is the only screen in the app without one. §5
 *    gives completion screens a `Done`; a BEEP ending returns to a hub, and the
 *    STUDY ending has no forward. Do not invent one.
 *  - FOOTNOTE ROW ONLY: `support`, and NOT `stop`. There is nothing left to
 *    stop. But she has just finished five days of disclosing her mental state,
 *    and that is precisely the moment not to remove the helpline. A considered
 *    exception to §4.5's pairing, not an oversight.
 *  - NO MOTION, NO TICK, NO CHECKMARK. ResearchKit animates one; §2.3 forbids
 *    animated progress and a tick is grade-adjacent on a screen whose whole
 *    discipline is not grading her.
 *
 * The words are config's (#92, AD-11 §2.5) and are not in `screens.json` yet,
 * so each reads `copy?.<key> ?? <literal>` — the shape Notifications.jsx
 * already uses. When config lands `screens.ending` this file changes by zero
 * lines.
 */
const ENDING_COPY = {
  title: "That's the end of Editrics",
  body_1: 'Thank you for taking part.',
  /*
   * THE LOAD-BEARING LINE. It is the exact negation of the false promise, and
   * it must not be softened into "for now". "the reminders have stopped" is the
   * sentence that stops her waiting for a phone that will never buzz again.
   */
  body_2: 'There are no more check-ins, and the reminders have stopped. Nothing else is needed from you.',
  body_3: 'Your answers are kept, as the consent form described.',
}

export default function Ending({ nav = null }) {
  const { data: bundle } = useConfigBundle()
  const copy = screensOf(bundle)?.ending ?? null
  const t = (key) => copy?.[key] ?? ENDING_COPY[key]

  return (
    /*
      `support` and no `stop`, no `action`. Screen renders the footnote row from
      those two flags alone, so the ActionBar composition specified above is a
      consequence of the props rather than a bespoke layout to keep in step.
    */
    <Screen support disposition="status" nav={nav}>
      <ScreenTitle>{t('title')}</ScreenTitle>
      {/*
        ScreenTitle owns --s-5 beneath the title; §2.1 asks for --s-6. The one
        remaining step is added here rather than by re-declaring an <h1>, which
        would put a second definition of the app's title type in the tree.
      */}
      <div className="flex flex-col gap-[var(--s-4)] pt-[var(--s-1)]">
        <p className="t-body text-[var(--ink-900)]">{t('body_1')}</p>
        <p className="t-body text-[var(--ink-700)]">{t('body_2')}</p>
        <p className="t-body text-[var(--ink-700)]">{t('body_3')}</p>
      </div>
      {/*
        DS-3 E2 also asks this screen to say how to request a copy or deletion.
        NOT WRITTEN: backend states neither route exists in v0 and that
        DRAFT-10's "ask us" implies a human process with no contact behind it —
        "do not imply either exists". Offering a route that is not there would
        be the same false promise this screen has just been cleared of. Raised
        with design and the operator rather than settled here.
      */}
    </Screen>
  )
}
